import { useTranslation } from 'react-i18next'
import { Button } from '../../../../common/components/button/Button'
import { getIcon } from '../../../../common/components/icons/utils'
import { Tag, TBusinessSettings } from '../../../../types/commonTypes'
import sharedStyles from '../../Settings.module.css'
import 'react-circular-progressbar/dist/styles.css'
import styles from './Players.module.css'
import { LicenceGraphs } from './LicenceGraphs'
import { PlayerAccountTable } from './PlayerAccountTable'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AddPlayersModal, Player } from './AddPlayersModal'
import { useBusinessSettings } from '../../../../contexts/BusinessSettingsContext'
import { ThreeDotsWithOverlay } from '../../../../common/components/loaders/ThreeDotsWithOverlay/ThreeDotsWithOverlay'
import { useNotification } from '../../../../contexts/NotificationContext'
import { managePlayerAccountTags, removeBusinessPlayerAccount } from '../../../../api/userApiService'
import { useUser } from '../../../../contexts/userContext'
import { dateTimeToLocalDateString } from '../../../../util/string'

type PlayersProps = {
  organizationName: string
  businessInfo: TBusinessSettings
  onViewAllLicenseHolders?: () => void
  onViewTags?: () => void
}

export type LicenceHolder = {
  name: string
  type: string
  fixedType?: string
}

export const Players: React.FC<PlayersProps> = ({
  organizationName,
  businessInfo,
  onViewAllLicenseHolders,
  onViewTags,
}) => {
  const { newBusinessInfo } = businessInfo?.businessInfo
  const { t } = useTranslation()
  const { pinSeatHolders, emailSeatHolders, licenceCount } = newBusinessInfo || {}
  const [isAddPlayersModalOpen, setIsAddPlayersModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const addPlayerButtonRef = useRef<HTMLButtonElement>(null)
  const {
    addPlayerAccounts,
    businessPlayerAccounts,
    getBusinessPlayerAccountInfo,
    resendPlayerAccountInvite,
    allPageOptions,
  } = useBusinessSettings()
  const { notifySuccess, notifyError } = useNotification()
  const { user } = useUser()

  const handleSubmitAddPlayers = useCallback(
    async (players: Player[]) => {
      const response = await addPlayerAccounts(players)
      if (response) getBusinessPlayerAccountInfo({ page: businessPlayerAccounts?.currentPage })
      return true
    },
    [addPlayerAccounts, businessPlayerAccounts?.currentPage, getBusinessPlayerAccountInfo],
  )

  const handleCloseAddPlayersModal = () => {
    setIsAddPlayersModalOpen(false)
    addPlayerButtonRef.current?.focus()
  }

  const getPlayerAccounts = useCallback(
    async (page?: number) => {
      return await getBusinessPlayerAccountInfo({ page: page })
    },
    [getBusinessPlayerAccountInfo],
  )

  useEffect(() => {
    getPlayerAccounts()
  }, [getBusinessPlayerAccountInfo, getPlayerAccounts])

  const handleOnRenewInvite = useCallback(
    async (playerEmail: string) => {
      const result = await resendPlayerAccountInvite(playerEmail)
      if (result) {
        getPlayerAccounts(businessPlayerAccounts?.currentPage)
      }
    },
    [businessPlayerAccounts?.currentPage, getPlayerAccounts, resendPlayerAccountInvite],
  )

  const handleManagePlayerAccountTags = useCallback(
    async (isRemoving: boolean, playerId: number | null, tag: Tag) => {
      if (!playerId) return
      if (!isRemoving) {
        setIsLoading(true)
        const response = await managePlayerAccountTags({ checkedPlayers: [playerId], checkedTags: [tag] })
        getPlayerAccounts(businessPlayerAccounts?.currentPage)
        setIsLoading(false)
        return response
      }
      if (isRemoving) {
        setIsLoading(true)
        const response = await managePlayerAccountTags({ checkedPlayers: [playerId], removedTags: [tag] })
        getPlayerAccounts(businessPlayerAccounts?.currentPage)
        setIsLoading(false)
        return response
      }
    },
    [businessPlayerAccounts, getPlayerAccounts],
  )

  const handleDeletePlayerAccount = useCallback(
    async (playerId: number) => {
      setIsLoading(true)
      const response = await removeBusinessPlayerAccount({ playerId })
      if (response.success) {
        notifySuccess({
          title: t(
            'settings_organization.player_accounts.delete_notification_success.title',
            'Player account deleted!',
          ),
          content: t(
            'settings_organization.player_accounts.delete_notification_success.content',
            'Player account deleted from business!',
          ),
        })
        getPlayerAccounts(businessPlayerAccounts?.currentPage)
      } else {
        notifyError({
          title: t(
            'settings_organization.player_accounts.delete_notification_error.title',
            'Player account not deleted!',
          ),
          content: t(
            'settings_organization.player_accounts.delete_notification_error.content',
            'Something went wrong! Try again later or contact support',
          ),
        })
      }
      setIsLoading(false)
    },
    [businessPlayerAccounts?.currentPage, getPlayerAccounts, notifyError, notifySuccess, t],
  )

  const emailLicenseHolders: LicenceHolder[] = (emailSeatHolders || '')
    .split(',')
    .filter((item) => item.length)
    .map((seatHolder): LicenceHolder => {
      return {
        name: seatHolder,
        type: t('settings_organization.players.email_type', 'eMail/Player account'),
      }
    })

  const pinLicenseHolders: LicenceHolder[] = (pinSeatHolders || '')
    .split(',')
    .filter((item) => item.length)
    .map((seatHolder): LicenceHolder => {
      return {
        name: seatHolder,
        type: t('settings_organization.players.qr_pin_type', 'QR/PIN'),
      }
    })

  const allLicenseHolders = emailLicenseHolders.concat(pinLicenseHolders)

  return (
    <>
      {isLoading && <ThreeDotsWithOverlay />}
      <div className={styles.section}>
        <div className={styles.titleContainer}>
          <h1 className={sharedStyles.sectionTitle}>
            {t('settings_organization.players.title', {
              organization_name: organizationName,
              player_count: newBusinessInfo
                ? emailLicenseHolders.concat(pinLicenseHolders)
                : businessInfo.businessInfo.totalPlayersCount,
              max_players: licenceCount || businessInfo.businessInfo.maxPlayers,
              defaultValue: '%{player_count}/%{max_players} %{organization_name} players',
            })}
          </h1>
        </div>
        {newBusinessInfo && (
          <>
            <div className={sharedStyles.licenceUserSection}>
              <div className={sharedStyles.titleContainer}>
                <p className={sharedStyles.licenceUserSectionTitle}>
                  {t('settings_organization.players.graph.title', 'Player licence users')}
                </p>
                <p>
                  {t(
                    'settings_organization.players.graph.subtitle',
                    'See how many player license seats are currently in use and the login methods used.',
                  )}
                </p>
              </div>

              <LicenceGraphs
                emailLicenseHolders={emailLicenseHolders.length}
                pinLicenseHolders={pinLicenseHolders.length}
                licenseValidUntilDate={businessInfo.businessInfo?.newBusinessInfo?.nextDateWhenLicencesFreed || ''}
                licenceEndDate={dateTimeToLocalDateString(businessInfo.businessInfo.validUntil, user?.language)}
                isRenewing={businessInfo.businessInfo.newBusinessInfo?.autoRenew || false}
              />
            </div>

            <div className={styles.actionsContainer}>
              <Button variant='outline-tiny' onClick={onViewAllLicenseHolders}>
                <span className='iconWrapperBig'>{getIcon('view')}</span>
                {t('settings_organization.players.view_licence_users_button_text', 'View licence users')}
              </Button>
            </div>
          </>
        )}
        <div className={sharedStyles.licenceUserSection}>
          <div className={sharedStyles.titleContainer}>
            <p className={sharedStyles.licenceUserSectionTitle}>
              {t('settings_organization.players.player_accounts.title', 'Player accounts:')}
            </p>
            <p>
              {t(
                'settings_organization.players.player_accounts.subtitle',
                'Create accounts for players to give them easy access to all games with one login',
              )}
            </p>
          </div>
          <PlayerAccountTable
            businessPlayerAccounts={businessPlayerAccounts}
            onRenewInvite={handleOnRenewInvite}
            deletePlayerAccount={handleDeletePlayerAccount}
            getBusinessPlayerAccountInfo={getBusinessPlayerAccountInfo}
            managePlayerAccountTags={handleManagePlayerAccountTags}
            allPageOptions={allPageOptions}
            hasLicences={!!businessInfo.businessInfo.newBusinessInfo}
          />
          <div className={styles.actionsContainer}>
            <Button ref={addPlayerButtonRef} variant='outline-tiny' onClick={() => setIsAddPlayersModalOpen(true)}>
              <span className='iconWrapperMedium'>{getIcon('plus')}</span>
              {t('settings_organization.players.player_accounts.button_text.add_new', 'Add new')}
            </Button>
            <Button ref={addPlayerButtonRef} variant='outline-tiny' onClick={onViewTags}>
              <span className='iconWrapperMedium'>{getIcon('text')}</span>
              {t('settings_organization.players.player_accounts.button_text.edit_tags', 'Edit tags')}
            </Button>
          </div>
          {isAddPlayersModalOpen && (
            <AddPlayersModal
              onSubmit={handleSubmitAddPlayers}
              onClose={handleCloseAddPlayersModal}
              organizationName={organizationName}
              playerCount={newBusinessInfo ? allLicenseHolders.length : businessPlayerAccounts?.totalPlayers || 0}
              maxPlayers={businessInfo.businessInfo.totalPlayerAccountCount || 0}
              limitPlayers={
                businessInfo.businessInfo.totalPlayerAccountCount &&
                businessInfo.businessInfo.totalPlayerAccountCount !== -1
                  ? true
                  : false
              }
            />
          )}
        </div>
      </div>
    </>
  )
}
